// src/components/InventoryAdjustment.tsx
import { useState, useEffect } from 'react';
import { createResource,getResources,getResourceById, updateResource} from '../../../router/api';
import { fr } from 'date-fns/locale';
import { format, isValid, parseISO } from 'date-fns';
import toast from 'react-hot-toast';

interface InventoryItem {
  id: number;
  productSku: string;
  productName: string;
  category: string;
  location: string;
  theoreticalStock: number;
  countedStock: number | null;
  difference: number | null;
  status: 'En Attente' | 'Compté' | 'Validé';
  countedBy?: string;
  countedAt?: string;
}

interface PastInventory {
  id: number;
  date: string | null;           // ← peut être null
  statut: 'En cours' | 'Clôturé';
  frozen: boolean;
  utilisateur: {
    id: number;
    name: string;
    email?: string;
  };
  stats: {
    total_produits: number;
    ecarts: number;
    sans_ecart: number;
  };
  items?: any[]; // pour le détail futur
}

interface InventoryStats {
  pending: number;
  counted: number;
  validated: number;
  totalDifference: number;
}

export default function InventoryAdjustment() {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [stats, setStats] = useState<InventoryStats>({ pending: 0, counted: 0, validated: 0, totalDifference: 0 });
  const [frozen, setFrozen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'En Attente' | 'Compté' | 'Validé'>('all');
  const [showCountModal, setShowCountModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [countValue, setCountValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [showReminder, setShowReminder] = useState(false);
  const [currentInventoryId, setCurrentInventoryId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'current' | 'history'>('current');

  // États pour l'historique
  const [pastInventories, setPastInventories] = useState<PastInventory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

    // Ajoute ces états en haut avec les autres
  const [showHistoryDetailModal, setShowHistoryDetailModal] = useState(false);
  const [selectedPastInventory, setSelectedPastInventory] = useState<PastInventory | null>(null);
  const [historyItems, setHistoryItems] = useState<any[]>([]);
  const [loadingHistoryDetail, setLoadingHistoryDetail] = useState(false);

  // === FONCTION SÉCURISÉE POUR FORMATER LES DATES ===
  const formatDateSafe = (dateString: string | null | undefined, fallback = 'Date inconnue') => {
    if (!dateString) return fallback;
    try {
      const date = parseISO(dateString);
      return isValid(date) ? format(date, 'dd MMMM yyyy', { locale: fr }) : fallback;
    } catch {
      return fallback;
    }
  };

  
  // Charger les données de l'inventaire actif
  const loadInventory = async () => {
    try {
      setLoading(true);
      const response = await getResources('inventaire/en-cours', {
        params: { search: searchTerm || undefined, status: statusFilter === 'all' ? undefined : statusFilter }
      });

      setItems(response.data.items || []);
      setStats(response.data.stats);
      setFrozen(response.data.frozen || false);
      setCurrentInventoryId(response.data.inventaire?.id || null);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setItems([]);
        setStats({ pending: 0, counted: 0, validated: 0, totalDifference: 0 });
      }
    } finally {
      setLoading(false);
    }
  };

  // Charger l'historique
  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await getResources('inventaire/historique');
      // Laravel paginate retourne { data: [...], meta: {...} }
      const list = response.data.data || response.data;
      setPastInventories(list);
    } catch (err) {
      console.error('Erreur chargement historique', err);
      setPastInventories([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // ... tes fonctions createNewInventory, toggleFreeze, handleCount, saveCount, validateAll inchangées ...

  useEffect(() => {
    if (activeTab === 'current') {
      loadInventory();
    } else {
      loadHistory();
    }
  }, [activeTab, searchTerm, statusFilter]);

// Fonction pour ouvrir le détail d'un inventaire historique
const openHistoryDetail = async (inv: PastInventory) => {
  setSelectedPastInventory(inv);
  setLoadingHistoryDetail(true);
  setShowHistoryDetailModal(true);

  try {
    const response = await getResources(`inventaire/${inv.id}/show`);
    // Ton endpoint retourne { data: { inventaire: { ..., items: [...] } } }
    setHistoryItems(response.data.data.inventaire.items || []);
    console.log(response.data.data.inventaire.items);
  } catch (err) {
    console.error('Erreur chargement détail inventaire', err);
    toast.error('Impossible de charger le détail de cet inventaire');
    setHistoryItems([]);
  } finally {
    setLoadingHistoryDetail(false);
  }
};
  // Créer un nouvel inventaire
  const createNewInventory = async () => {
    if (!confirm('Créer un nouvel inventaire physique ? Tous les stocks seront gelés pour comptage.')) return;

    try {
      const response = await createResource('inventaire', {
        date: new Date().toISOString().split('T')[0]
      });

      if (response.data.reminder) {
        setShowReminder(true);
        setTimeout(() => setShowReminder(false), 8000);
      }

      alert('Nouvel inventaire créé avec succès !');
      loadInventory();
    } catch (err) {
      alert('Erreur lors de la création de l\'inventaire');
    }
  };

  // Geler / Dégeler
  const toggleFreeze = async () => {
    if (!currentInventoryId) return;
    try {
      await createResource(`inventaire/${currentInventoryId}/freeze`, { frozen: !frozen });
      setFrozen(!frozen);
    } catch (err) {
      alert('Erreur lors du changement de statut du gel');
    }
  };

  // Compter un article
  const handleCount = (item: InventoryItem) => {
    setSelectedItem(item);
    setCountValue('');
    setShowCountModal(true);
  };

  const saveCount = async () => {
    if (!selectedItem || !countValue || !currentInventoryId) return;

    try {
      await updateResource(`inventaire/${currentInventoryId}/items`,selectedItem.id, {
        countedStock: parseInt(countValue)
      });

      loadInventory();
      setShowCountModal(false);
      setSelectedItem(null);
      setCountValue('');
    } catch (err) {
      alert('Erreur lors de l\'enregistrement du comptage');
    }
  };

  // Valider tous les comptés
  const validateAll = async () => {
    if (!currentInventoryId || stats.counted === 0) return;
    if (!confirm(`Valider ${stats.counted} articles comptés ?`)) return;

    try {
      await createResource(`inventaire/${currentInventoryId}/valider`,{});
      alert('Tous les articles ont été validés !');
      loadInventory();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erreur lors de la validation');
    }
  };

  useEffect(() => {
    loadInventory();
  }, [searchTerm, statusFilter]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'En Attente': return 'bg-gray-100 text-gray-800';
      case 'Compté': return 'bg-blue-100 text-blue-800';
      case 'Validé': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100';
    }
  };

  return (
    <>
      {/* Rappel 3 mois */}
      {showReminder && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-orange-500 text-white px-8 py-4 rounded-xl shadow-2xl flex items-center gap-3 animate-pulse">
          <i className="ri-alarm-warning-line text-2xl"></i>
          <span className="font-bold">Rappel : Plus de 3 mois depuis le dernier inventaire !</span>
        </div>
      )}

      <div className="space-y-6">
        {/* Header avec bouton création */}
        {/* Onglets */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('current')}
              className={`flex-1 py-4 px-6 font-medium text-lg transition-colors ${activeTab === 'current'
                ? 'text-purple-600 border-b-4 border-purple-600'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              <i className="ri-focus-3-line mr-2"></i> Inventaire en cours
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 py-4 px-6 font-medium text-lg transition-colors ${activeTab === 'history'
                ? 'text-purple-600 border-b-4 border-purple-600'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              <i className="ri-history-line mr-2"></i> Historique
            </button>
          </div>
        </div>
        {/* === ONGLET : INVENTAIRE EN COURS === */}
        {activeTab === 'current' && (
          <>
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <h2 className="text-3xl font-bold mb-2">Inventaire Physique</h2>
                  <p className="text-purple-100 text-lg">
                    {frozen ? 'Stock gelé pour inventaire' : 'Stock en temps réel'}
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={toggleFreeze}
                    className={`px-6 py-3 rounded-lg font-medium transition-all ${frozen
                      ? 'bg-white text-purple-600 hover:bg-purple-50'
                      : 'bg-purple-700 hover:bg-purple-800'
                      }`}
                  >
                    {frozen ? 'Dégeler' : 'Geler'} le Stock
                  </button>

                {!currentInventoryId && (
                    <button
                      onClick={createNewInventory}
                      className="px-6 py-3 bg-green-500 hover:bg-green-600 rounded-lg font-medium transition-all flex items-center gap-2"
                    >
                      <i className="ri-add-box-line text-xl"></i>
                      Nouvel Inventaire
                    </button>
                  )}

                </div>
              </div>
            </div>

            {/* Cartes statistiques */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600">En Attente</p>
                    <p className="text-3xl font-bold text-gray-900 mt-2">{stats.pending}</p>
                  </div>
                  <i className="ri-time-line text-4xl text-gray-400"></i>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600">Comptés</p>
                    <p className="text-3xl font-bold text-blue-600 mt-2">{stats.counted}</p>
                  </div>
                  <i className="ri-checkbox-line text-4xl text-blue-500"></i>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600">Validés</p>
                    <p className="text-3xl font-bold text-green-600 mt-2">{stats.validated}</p>
                  </div>
                  <i className="ri-checkbox-circle-line text-4xl text-green-500"></i>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600">Écart Total</p>
                    <p className={`text-3xl font-bold mt-2 ${stats.totalDifference < 0 ? 'text-red-600' : stats.totalDifference > 0 ? 'text-green-600' : 'text-gray-900'}`}>
                      {stats.totalDifference > 0 ? '+' : ''}{stats.totalDifference}
                    </p>
                  </div>
                  <i className="ri-bar-chart-line text-4xl text-orange-500"></i>
                </div>
              </div>
            </div>

            {/* Filtres + tableau */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <input
                  type="text"
                  placeholder="Rechercher produit, SKU, emplacement..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />

                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as any)}
                  className="px-4 py-3 border rounded-lg"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="En Attente">En Attente</option>
                  <option value="Compté">Compté</option>
                  <option value="Validé">Validé</option>
                </select>

                <button
                  onClick={validateAll}
                  disabled={stats.counted === 0}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Valider Tous ({stats.counted})
                </button>
              </div>

              {loading ? (
                <div className="text-center py-20">
                  <i className="ri-loader-4-line text-6xl text-purple-500 animate-spin"></i>
                </div>
              ) : items.length === 0 ? (
                <div className="text-center py-20">
                  <i className="ri-inbox-line text-8xl text-gray-300"></i>
                  <p className="text-xl text-gray-500 mt-4">
                    Aucun inventaire en cours.<br />
                    <button onClick={createNewInventory} className="text-purple-600 font-bold underline mt-4">
                      Créer un nouvel inventaire
                    </button>
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-4 px-4">SKU</th>
                        <th className="text-left py-4 px-4">Produit</th>
                        <th className="text-center py-4 px-4">Emplacement</th>
                        <th className="text-center py-4 px-4">Théorique</th>
                        <th className="text-center py-4 px-4">Compté</th>
                        <th className="text-center py-4 px-4">Écart</th>
                        <th className="text-center py-4 px-4">Statut</th>
                        <th className="text-left py-4 px-4">Compté par</th>
                        <th className="text-center py-4 px-4">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={item.id} className="border-t hover:bg-gray-50">
                          <td className="py-4 px-4 font-mono">{item.productSku}</td>
                          <td className="py-4 px-4">
                            <div>
                              <p className="font-medium">{item.productName}</p>
                              <p className="text-sm text-gray-500">{item.category?.nom}</p>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className="bg-gray-100 px-3 py-1 rounded-full text-sm">{item.location}</span>
                          </td>
                          <td className="py-4 px-4 text-center font-bold">{item.theoreticalStock}</td>
                          <td className="py-4 px-4 text-center font-bold text-blue-600">
                            {item.countedStock ?? '-'}
                          </td>
                          <td className="py-4 px-4 text-center font-bold">
                            {item.difference !== null && (
                              <span className={item.difference < 0 ? 'text-red-600' : item.difference > 0 ? 'text-green-600' : 'text-gray-600'}>
                                {item.difference > 0 ? '+' : ''}{item.difference}
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-4 text-center">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(item.status)}`}>
                              {item.status}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-sm">
                            {item.countedBy ? (
                              <div>
                                <p>{item.countedBy}</p>
                                <p className="text-gray-500">{item.countedAt}</p>
                              </div>
                            ) : '-'}
                          </td>
                          <td className="py-4 px-4 text-center">
                            {item.status !== 'Validé' && (
                              <button
                                onClick={() => handleCount(item)}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                              >
                                Compter
                              </button>
                            )}
                            {item.status === 'Validé' && <span className="text-green-600 font-medium">Validé</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* === ONGLET : HISTORIQUE === */}
        {activeTab === 'history' && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-xl p-6 text-white shadow-lg">
              <h2 className="text-3xl font-bold mb-2">Historique des Inventaires</h2>
              <p className="text-indigo-100 text-lg">Consultez tous les inventaires passés</p>
            </div>

            {loadingHistory ? (
              <div className="text-center py-20">
                <i className="ri-loader-4-line text-6xl text-indigo-500 animate-spin"></i>
              </div>
            ) : pastInventories.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-xl border">
                <i className="ri-inbox-line text-8xl text-gray-300 mb-6"></i>
                <p className="text-xl text-gray-500">Aucun inventaire terminé pour le moment</p>
              </div>
            ) : (
              <div className="grid gap-6">
                {pastInventories.map((inv) => (
                  <div key={inv.id} className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-3">
                          <span className={`px-4 py-2 rounded-full text-sm font-bold ${getStatusColor(inv.statut)}`}>
                            {inv.statut}
                          </span>
                          <span className="text-sm text-gray-600">
                            par <strong>{inv.utilisateur.name}</strong>
                          </span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div>
                            <p className="text-sm text-gray-600">Date de l'inventaire</p>
                            <p className="text-xl font-bold">
                              {formatDateSafe(inv.date)}
                            </p>
                          </div>

                          <div>
                            <p className="text-sm text-gray-600">Écart total</p>
                            <p className={`text-2xl font-bold ${inv.stats.ecarts < 0 ? 'text-red-600' : inv.stats.ecarts > 0 ? 'text-green-600' : 'text-gray-900'}`}>
                              {inv.stats.ecarts > 0 ? '+' : ''}{inv.stats.ecarts}
                            </p>
                          </div>

                          <div>
                            <p className="text-sm text-gray-600">Produits concernés</p>
                            <p className="text-xl font-bold">{inv.stats.total_produits}</p>
                          </div>
                        </div>

                        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-xs text-gray-600">Total articles</p>
                            <p className="text-lg font-bold">{inv.stats.total_produits}</p>
                          </div>
                          <div className="bg-red-50 p-3 rounded-lg">
                            <p className="text-xs text-red-700">Avec écart</p>
                            <p className="text-lg font-bold text-red-700">{inv.stats.ecarts}</p>
                          </div>
                          <div className="bg-green-50 p-3 rounded-lg">
                            <p className="text-xs text-green-700">Sans écart</p>
                            <p className="text-lg font-bold text-green-700">{inv.stats.sans_ecart}</p>
                          </div>
                          <div className="bg-purple-50 p-3 rounded-lg">
                            <p className="text-xs text-purple-700">Taux exactitude</p>
                            <p className="text-lg font-bold text-purple-700">
                              {inv.stats.total_produits > 0
                                ? Math.round((inv.stats.sans_ecart / inv.stats.total_produits) * 100)
                                : 100}%
                            </p>
                          </div>
                        </div>
                      </div>

                      <div>
                        <button
                          onClick={() => openHistoryDetail(inv)}
                          className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 transition-all"
                        >
                          <i className="ri-eye-line"></i>
                          Voir détail
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Modal de comptage */}
        {showCountModal && selectedItem && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
              <div className="p-6 border-b">
                <h3 className="text-2xl font-bold">Saisir le comptage</h3>
              </div>
              <div className="p-6 space-y-6">
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="font-bold text-lg">{selectedItem.productName}</p>
                  <p className="text-sm text-gray-600">SKU: {selectedItem.productSku}</p>
                  <p className="text-sm mt-2">Emplacement: <strong>{selectedItem.location}</strong></p>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-2">Stock théorique</p>
                  <p className="text-4xl font-bold text-gray-800">{selectedItem.theoreticalStock}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Quantité réelle comptée <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={countValue}
                    onChange={(e) => setCountValue(e.target.value)}
                    className="w-full px-4 py-4 text-2xl border-2 rounded-lg focus:border-purple-500"
                    autoFocus
                  />
                </div>

                {countValue && (
                  <div className={`p-4 rounded-lg border-2 ${parseInt(countValue) - selectedItem.theoreticalStock < 0 ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-300'}`}>
                    <p className="text-sm">Écart</p>
                    <p className={`text-3xl font-bold ${parseInt(countValue) - selectedItem.theoreticalStock < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {(parseInt(countValue) - selectedItem.theoreticalStock) > 0 ? '+' : ''}
                      {parseInt(countValue) - selectedItem.theoreticalStock}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 p-6 border-t">
                <button
                  onClick={() => {
                    setShowCountModal(false);
                    setSelectedItem(null);
                    setCountValue('');
                  }}
                  className="px-6 py-3 border rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={saveCount}
                  disabled={!countValue}
                  className="px-8 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300"
                >
                  Enregistrer
                </button>
              </div>
            </div>
          </div>
        )}
              {/* === MODALE DÉTAIL INVENTAIRE HISTORIQUE === */}
        {showHistoryDetailModal && selectedPastInventory && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      Détail Inventaire du {formatDateSafe(selectedPastInventory.date)}
                    </h3>
                    <div className="flex items-center gap-4 mt-3">
                      <span className={`px-4 py-2 rounded-full text-sm font-bold ${getStatusColor(selectedPastInventory.statut)}`}>
                        {selectedPastInventory.statut}
                      </span>
                      <span className="text-sm text-gray-600">
                        Réalisé par <strong>{selectedPastInventory.utilisateur.name}</strong>
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowHistoryDetailModal(false);
                      setSelectedPastInventory(null);
                      setHistoryItems([]);
                    }}
                    className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </div>
              </div>

              {/* Stats rapides */}
              <div className="p-6 bg-gray-50 border-b border-gray-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                  <div>
                    <p className="text-sm text-gray-600">Articles totaux</p>
                    <p className="text-3xl font-bold text-gray-900">{selectedPastInventory.stats.total_produits}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Avec écart</p>
                    <p className="text-3xl font-bold text-red-600">{selectedPastInventory.stats.ecarts}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Sans écart</p>
                    <p className="text-3xl font-bold text-green-600">{selectedPastInventory.stats.sans_ecart}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Taux exactitude</p>
                    <p className="text-3xl font-bold text-purple-600">
                      {selectedPastInventory.stats.total_produits > 0
                        ? Math.round((selectedPastInventory.stats.sans_ecart / selectedPastInventory.stats.total_produits) * 100)
                        : 100}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Liste des articles */}
              <div className="p-6">
                {loadingHistoryDetail ? (
                  <div className="text-center py-12">
                    <i className="ri-loader-4-line text-6xl text-indigo-500 animate-spin"></i>
                  </div>
                ) : historyItems.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <i className="ri-inbox-line text-6xl mb-4"></i>
                    <p>Aucun article dans cet inventaire</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left py-4 px-6">Produit</th>
                          <th className="text-center py-4 px-6">Emplacement</th>
                          <th className="text-center py-4 px-6">Théorique</th>
                          <th className="text-center py-4 px-6">Compté</th>
                          <th className="text-center py-4 px-6">Écart</th>
                          <th className="text-center py-4 px-6">Statut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {historyItems.map((item, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="py-4 px-6">
                              <p className="font-medium">{item.produit?.nom || 'Produit inconnu'}</p>
                            </td>
                            <td className="py-4 px-6 text-center">
                              <span className="bg-gray-100 px-3 py-1 rounded-full text-sm">
                                {item.emplacement?.code || item.emplacement?.nom || 'Non défini'}
                              </span>
                            </td>
                            <td className="py-4 px-6 text-center font-bold">{item.theorique}</td>
                            <td className="py-4 px-6 text-center font-bold text-blue-600">
                              {item.compte ?? '-'}
                            </td>
                            <td className="py-4 px-6 text-center font-bold">
                              {item.ecart !== 0 && (
                                <span className={item.ecart < 0 ? 'text-red-600' : 'text-green-600'}>
                                  {item.ecart > 0 ? '+' : ''}{item.ecart}
                                </span>
                              )}
                              {item.ecart === 0 && <span className="text-gray-600">0</span>}
                            </td>
                            <td className="py-4 px-6 text-center">
                              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(item.status)}`}>
                                {item.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}